import React, { useState, useEffect } from 'react'
import { useFormik } from 'formik';
import { Link, useNavigate } from 'react-router-dom'
import * as Yup from 'yup';
import Select from "react-select";
import { Button } from '@app/components';
import { getRoleGroups, getRolesByGroupName } from '@app/services/role';
import { getUserById, updateUser, viewUserById } from '@app/services/user';
import { unassignUserFromCentre } from '@app/services/centre';
import { unassignUserFromFranchise } from '@app/services/franchise';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import { useDecodedParam } from '@app/hooks/useDecodedParam';

const EditUser = () => {
    const userStatus = [{id:'active', name:'Active'}, {id:'inactive', name:'Inactive'}]

    let obj: any = {}
    const [roleGroups, setroleGroups] = useState([]);
    const [FMSroles, setFMSroles] = useState([]);
    const [FCroles, setFCroles] = useState([]);
    const [fmsRoleFlag, setFmsRoleFlag] = useState(false);
    const [fcRoleFlag, setFcRoleFlag] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [userData, setUserData] = useState(obj);
    const [showImpersonation, setShowImpersonation] = useState(false);
    const [initialFcARoleIds, setInitialFcARoleIds] = useState<number[]>([]);
    const [centreGroups, setCentreGroups] = useState<{ roleId: number; roleName: string; centres: any[] }[]>([]);
    // Franchises this user owns, grouped by role -- mirrors centreGroups, but
    // for the Franchise Owner role (attachment lives in user_franchise_roles,
    // not user_centre_roles, so it can't be removed via handleRemoveCentre).
    const [franchiseGroups, setFranchiseGroups] = useState<{ roleId: number; roleName: string; franchises: any[] }[]>([]);
    const navigate = useNavigate();
    const id = useDecodedParam('encodedId', '/users');

    useEffect(() => {
        getRoleGroupsData();
        getUserDetailsById(id);
        fetchUserCentreGroups(id);
    }, []);

    useEffect(() => {
        if(fmsRoleFlag){
            getRolesByGroupNameData({roleGroup: 'FMS-A'});
        }else{
            setFieldValue("fms_a_role", '');
            setFieldValue("impersonation_enabled", false);
            setShowImpersonation(false);
        }
    }, [fmsRoleFlag]);

    useEffect(() => {
        if(fcRoleFlag){
            getRolesByGroupNameData({roleGroup: 'FC-A'});
        }else{
            setFieldValue("fc_a_role", []);
        }
    }, [fcRoleFlag]);

    const getUserDetailsById = async (id) => {
        try {
            const resp = await getUserById(id);
            if(resp.success === true){
                setUserData(resp.collections);
                if(resp?.collections?.roles?.length > 0){
                    const isFMSfound = resp.collections.roles.some(function(el) {
                        return el['group'] === "FMS-A";
                    })
                    setFmsRoleFlag(isFMSfound);
            
                    const isFCfound = resp.collections.roles.some(function(el) {
                        return el['group'] === "FC-A";
                    })
                    setFcRoleFlag(isFCfound);
            
                    const fcARoleIds = [];
                    resp.collections.roles.map((option) => {
                        if(option.group === "FMS-A"){
                            setFieldValue("fms_a_role", option.id);
                            setShowImpersonation(option.slug === 'admin');
                        }
                        if(option.group === "FC-A"){
                            fcARoleIds.push(option.id);
                        }
                    })
                    setFieldValue("fc_a_role", fcARoleIds);
                    setInitialFcARoleIds(fcARoleIds);
                    
                    setFieldValue(
                    "role_group",
                    [].slice.call(resp.collections.roles).map((option) => option.group)
                    );
                }
            }else{
                toast.error(resp?.errors[0]?.msg);
                navigate('/users');
            }
        } catch (error: any) {
            toast.error(error?.response?.message || 'Failed');
        }
    };

    // Loads the centres this user holds, grouped by role, so the Edit User
    // page can show a "remove centre from role" control (mirrors the Centre
    // Staff/Users table on ViewCentre.tsx) and so role removal can be
    // validated against centres still attached to that role.
    //
    // Also loads the franchises this user owns (Franchise Owner role), grouped
    // the same way, so removing that role can be blocked while franchises are
    // still attached -- same reasoning as the centre case above.
    const fetchUserCentreGroups = async (userId) => {
        try {
            const resp = await viewUserById(userId);
            if (resp?.success === true) {
                const roleList = resp?.collections?.roles || [];

                const centreRecords = resp?.collections?.centre || [];
                const groupsByRole: Record<number, { roleId: number; roleName: string; centres: any[] }> = {};
                centreRecords.forEach((record: any) => {
                    const roleId = record.role_id;
                    const roleName = roleList.find((r: any) => r.id === roleId)?.name || 'Other';
                    if (!groupsByRole[roleId]) {
                        groupsByRole[roleId] = { roleId, roleName, centres: [] };
                    }
                    if (!groupsByRole[roleId].centres.some((c: any) => c.centre_id === record.centre_id)) {
                        groupsByRole[roleId].centres.push(record);
                    }
                });
                setCentreGroups(Object.values(groupsByRole));

                // franchise-owner is identified by slug, not a hardcoded id --
                // role ids differ per environment (see rbac/constants.js).
                const franchiseOwnerRoleId = roleList.find((r: any) => r.slug === 'franchise-owner')?.id;
                const franchiseRecords = (resp?.collections?.franchise || [])
                    .filter((record: any) => record.role_id === franchiseOwnerRoleId);
                const franchiseGroupsByRole: Record<number, { roleId: number; roleName: string; franchises: any[] }> = {};
                franchiseRecords.forEach((record: any) => {
                    const roleId = record.role_id;
                    const roleName = roleList.find((r: any) => r.id === roleId)?.name || 'Franchise Owner';
                    if (!franchiseGroupsByRole[roleId]) {
                        franchiseGroupsByRole[roleId] = { roleId, roleName, franchises: [] };
                    }
                    if (!franchiseGroupsByRole[roleId].franchises.some((f: any) => f.id === record.id)) {
                        franchiseGroupsByRole[roleId].franchises.push(record);
                    }
                });
                setFranchiseGroups(Object.values(franchiseGroupsByRole));
            } else {
                setCentreGroups([]);
                setFranchiseGroups([]);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load centres attached to this user');
        }
    };

    // Removing is role-specific: a user can hold this centre under more than
    // one role, so only the (user, centre, role) triple that was clicked is
    // dropped, via the same API used on the Centre Staff/Users table.
    const handleRemoveCentre = (centre: any, roleId: number, roleName: string) => {
        confirmAlert({
            customUI: ({ onClose }) => (
                <div className="card p-4" style={{ minWidth: 320 }}>
                    <h5>Remove Centre</h5>
                    <p>Remove <strong>{centre.name}</strong> from the <strong>{roleName}</strong> role for this user?</p>
                    <div className="d-flex justify-content-end">
                        <button className="btn btn-danger btn-sm mr-2" onClick={async () => {
                            onClose();
                            try {
                                const resp = await unassignUserFromCentre({ user_id: id, centre_id: centre.centre_id, role_id: roleId });
                                if (resp?.success === true) {
                                    toast.success(`${centre.name} removed from ${roleName}`);
                                    fetchUserCentreGroups(id);
                                } else {
                                    toast.error(resp?.errors?.[0]?.msg || resp?.message || 'Failed to remove centre');
                                }
                            } catch (error: any) {
                                toast.error(error?.message || 'Internal Server Error');
                            }
                        }}>Remove</button>
                        <button className="btn btn-default btn-sm" onClick={onClose}>Cancel</button>
                    </div>
                </div>
            )
        });
    };

    // Removing is role-specific, same as handleRemoveCentre: a user can be
    // attached to a franchise under more than one role in principle, so only
    // the (user, franchise, role) triple that was clicked is dropped.
    // The API rejects this with 400 when the user is the franchise's last
    // remaining attached user -- surfaced via the returned error message.
    const handleRemoveFranchise = (franchise: any, roleId: number, roleName: string) => {
        confirmAlert({
            customUI: ({ onClose }) => (
                <div className="card p-4" style={{ minWidth: 320 }}>
                    <h5>Remove Franchise</h5>
                    <p>Remove <strong>{franchise.name}</strong> from the <strong>{roleName}</strong> role for this user?</p>
                    <div className="d-flex justify-content-end">
                        <button className="btn btn-danger btn-sm mr-2" onClick={async () => {
                            onClose();
                            try {
                                const resp = await unassignUserFromFranchise({ user_id: id, franchise_id: franchise.id, role_id: roleId });
                                if (resp?.success === true) {
                                    toast.success(`${franchise.name} removed from ${roleName}`);
                                    fetchUserCentreGroups(id);
                                } else {
                                    toast.error(resp?.errors?.[0]?.msg || resp?.message || 'Failed to remove franchise');
                                }
                            } catch (error: any) {
                                toast.error(error?.message || 'Internal Server Error');
                            }
                        }}>Remove</button>
                        <button className="btn btn-default btn-sm" onClick={onClose}>Cancel</button>
                    </div>
                </div>
            )
        });
    };

    const getRoleGroupsData = async () => {
       try {
            const roleGroupsResp = await getRoleGroups();
            if (roleGroupsResp.success === true) {
                setroleGroups(roleGroupsResp.collections);
            } else {
                toast.error(roleGroupsResp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.response?.message || 'Failed');
        }
    };

    const getRolesByGroupNameData = async (payload) => {
        const rolesResp = await getRolesByGroupName(payload);
        if(payload.roleGroup === 'FMS-A'){
            setFMSroles(rolesResp.collections);
        }
        if(payload.roleGroup === 'FC-A'){
            setFCroles(rolesResp.collections);
        }
    };

    const putUser = async (payload) => {
        try {
            setLoading(true);
            const resp = await updateUser(id, payload);
            if(resp.success === true){
                toast.success(resp?.message);
                setLoading(false);
                navigate('/users');
            }else{
                toast.error(resp?.errors[0]?.msg);
                setLoading(false);
            }
          } catch (error: any) {
            setLoading(false);
            toast.error(error.response.message || 'Failed');
          }
    };

    const { handleChange, values, handleSubmit, touched, errors, setFieldValue } = useFormik({
        initialValues: {
            first_name: userData?.first_name || '',
            last_name: userData?.last_name || '',
            email: userData?.email || '',
            alt_email: userData?.alt_email || '',
            phone: userData?.phone || '',
            alt_phone: userData?.alt_phone || '',
            address: userData?.address || '',
            role_group: [],
            status: userData?.status || '',
            fms_a_role: '',
            fc_a_role: [],
            impersonation_enabled: userData?.impersonation_enabled ?? false,
        },
        validationSchema: Yup.object({
            first_name: Yup.string().required('Please enter first name'),
            last_name: Yup.string().required('Please enter last name'),
            email: Yup.string()
                .required("Please enter email address")
                .email("Enter valid email"),
            alt_email: Yup.string()
                .email("Enter valid email"),
            phone: Yup.string()//.required('Please enter mobile number')
            .matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
                "Please enter valid number"
            ),
            alt_phone: Yup.string().matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
            "Please enter valid number"
            ),
            // address: Yup.string().required('Please enter address'),
            role_group: Yup.array()
            .of(Yup.string().trim())
            .min(1, "Please select at least 1 role group")
            .required("Please select role group"),
            status: Yup.string().required('Please select status'),
            fms_a_role: Yup.string().when("role_group", {
                is: (val: string | string[]) => val.includes('FMS-A'),
                then: Yup.string().required('Please select role'),
            }),
            fc_a_role: Yup.array().when("role_group", {
                is: (val: string | string[]) => val.includes('FC-A'),
                then: Yup.array().min(1, "Please select at least 1 role").required('Please select role'),
            }).test(
                'no-centres-attached-on-removed-role',
                'This role still has centre(s) attached. Remove the centre(s) from this role below before removing the role itself.',
                (fcARoleIds: number[] | undefined) => {
                    const selectedIds = fcARoleIds || [];
                    const removedRoleIds = initialFcARoleIds.filter((roleId) => !selectedIds.includes(roleId));
                    return !removedRoleIds.some((roleId) => {
                        const group = centreGroups.find((g) => g.roleId === roleId);
                        return group && group.centres.length > 0;
                    });
                }
            ).test(
                'no-franchises-attached-on-removed-role',
                'This role still has franchise(s) attached. Remove the franchise(s) from this role below before removing the role itself.',
                (fcARoleIds: number[] | undefined) => {
                    const selectedIds = fcARoleIds || [];
                    const removedRoleIds = initialFcARoleIds.filter((roleId) => !selectedIds.includes(roleId));
                    return !removedRoleIds.some((roleId) => {
                        const group = franchiseGroups.find((g) => g.roleId === roleId);
                        return group && group.franchises.length > 0;
                    });
                }
            )
        }),
        onSubmit: (values) => {
            values.first_name = (values.first_name).trim();
            values.last_name = (values.last_name).trim();
            putUser(values);
        },
        enableReinitialize: true,
    });

    const roleGroupChange = (options: readonly typeof Option[], setFieldValue) => {
        const isFMSfound = options.some(function(el) {
            return el['name'] === "FMS-A";
        })
        setFmsRoleFlag(isFMSfound);

        const isFCfound = options.some(function(el) {
            return el['name'] === "FC-A";
        })
        setFcRoleFlag(isFCfound);

        setFieldValue(
          "role_group",
          [].slice.call(options).map((option) => option.name)
        );
    };

  return (
    <div>
        <section className="content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card card-primary">
                            <div className="card-header">
                                <h3 className="card-title">Update User</h3>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">First Name <span className="required">*</span></label>
                                                <input type="text" className="form-control" id="exampleInputEmail1" placeholder="Enter first name" 
                                                    name="first_name" 
                                                    onChange={handleChange} 
                                                    value={values.first_name}
                                                />
                                                {(touched.first_name && errors.first_name) && (
                                                    <div className="text-danger">
                                                        {errors.first_name}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputPassword1">Last Name <span className="required">*</span></label>
                                                <input type="text" className="form-control" id="exampleInputPassword1" placeholder="Enter last name" 
                                                    name="last_name" 
                                                    onChange={handleChange} 
                                                    value={values.last_name}
                                                />
                                                {(touched.last_name && errors.last_name) && (
                                                    <div className="text-danger">
                                                        {errors.last_name}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Email <span className="required">*</span></label>
                                                <input type="email" className="form-control" id="exampleInputEmail1" placeholder="Enter email" 
                                                    name="email" 
                                                    onChange={handleChange}
                                                    value={values.email}
                                                />
                                                {(touched.email && errors.email) && (
                                                    <div className="text-danger">
                                                        {errors.email}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Alternate Email </label>
                                                <input type="email" className="form-control" id="exampleInputEmail1" placeholder="Enter alternate email" 
                                                    name="alt_email" 
                                                    onChange={handleChange}
                                                    value={values.alt_email}
                                                />
                                                {(touched.alt_email && errors.alt_email) && (
                                                    <div className="text-danger">
                                                        {errors.alt_email}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputPassword1">Mobile {/*<span className="required">*</span> */}</label>
                                                <input type="text" className="form-control" id="exampleInputPassword1" placeholder="Enter mobile number" 
                                                    name="phone" 
                                                    onChange={handleChange} 
                                                    value={values.phone}
                                                />
                                                {(touched.phone && errors.phone) && (
                                                    <div className="text-danger">
                                                        {errors.phone}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Alternate Number</label>
                                                <input type="text" className="form-control" id="exampleInputEmail1" placeholder="Enter alternate number" 
                                                    name="alt_phone" 
                                                    onChange={handleChange} 
                                                    value={values.alt_phone}
                                                />
                                                {(touched.alt_phone && errors.alt_phone) && (
                                                    <div className="text-danger">
                                                        {errors.alt_phone}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Address {/*<span className="required">*</span> */}</label>
                                                <textarea 
                                                    className="form-control" 
                                                    id="exampleInputEmail1" 
                                                    placeholder="Enter address" 
                                                    name="address" 
                                                    onChange={handleChange} 
                                                    value={values.address}
                                                ></textarea>
                                                {(touched.address && errors.address) && (
                                                    <div className="text-danger">
                                                        {errors.address}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Role Group <span className="required">*</span></label>
                                                <Select
                                                    placeholder={<div>-- Please select --</div>}
                                                    isMulti
                                                    options={roleGroups}
                                                    closeMenuOnSelect={false}
                                                    getOptionLabel={(option)=>option.name}
                                                    getOptionValue={(option)=>option.name}
                                                    name="role_group"
                                                    onChange={ (e) => {roleGroupChange(e, setFieldValue)}}
                                                    value={
                                                        roleGroups?.length
                                                        ? roleGroups.filter((obj) =>
                                                            values.role_group.includes(obj.name)
                                                            )
                                                        : null
                                                    } // set selected values
                                                />
                                                {(touched.role_group && errors.role_group) && (
                                                    <div className="text-danger">
                                                        {errors.role_group}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Status <span className="required">*</span></label>
                                                <select
                                                    className="form-control"
                                                    name="status"
                                                    onChange={handleChange} 
                                                    value={values.status}
                                                >
                                                    <option value="">--Please select--</option>
                                                    {userStatus.map((obj) => (
                                                        <option key={obj.id} value={obj.id}>
                                                            {obj.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {(touched.status && errors.status) && (
                                                    <div className="text-danger">
                                                        {errors.status}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        { fmsRoleFlag && showImpersonation && (
                                            <div className="col-sm-4">
                                                <div className="form-group">
                                                    <label>Impersonation</label>
                                                    <div className="custom-control custom-switch mt-1">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id="impersonation_enabled"
                                                            name="impersonation_enabled"
                                                            checked={!!values.impersonation_enabled}
                                                            onChange={(e) => setFieldValue('impersonation_enabled', e.target.checked)}
                                                        />
                                                        <label className="custom-control-label" htmlFor="impersonation_enabled">
                                                            {values.impersonation_enabled ? 'Enabled' : 'Disabled'}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        { fmsRoleFlag && (
                                            <div className="col-sm-4">
                                                <div className="form-group">
                                                    <label htmlFor="exampleInputEmail1">FMS-A Role <span className="required">*</span></label>
                                                    <select 
                                                        className="form-control"
                                                        name="fms_a_role"
                                                        onChange={(e) => {
                                                            handleChange(e);
                                                            const selected = FMSroles.find((r: any) => r.id == e.target.value);
                                                            const isAdminRole = selected?.slug === 'admin';
                                                            setShowImpersonation(isAdminRole);
                                                            if (!isAdminRole) setFieldValue('impersonation_enabled', false);
                                                        }}
                                                        value={values.fms_a_role}
                                                    >
                                                        <option value="">--Please select--</option>
                                                        {FMSroles.map((obj) => (
                                                            <option key={obj.id} value={obj.id}>
                                                                {obj.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {(touched.fms_a_role && errors.fms_a_role) && (
                                                        <div className="text-danger">
                                                            {errors.fms_a_role}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        { fcRoleFlag && (
                                            <div className="col-sm-4">
                                                <div className="form-group">
                                                    <label htmlFor="exampleInputEmail1">FC-A Role <span className="required">*</span></label>
                                                    <Select
                                                        placeholder={<div>-- Please select --</div>}
                                                        isMulti
                                                        options={FCroles}
                                                        closeMenuOnSelect={false}
                                                        getOptionLabel={(option: any)=>option.name}
                                                        getOptionValue={(option: any)=>option.id}
                                                        name="fc_a_role"
                                                        onChange={(selected) => {
                                                            const newIds = (selected || []).map((option: any) => option.id);
                                                            // Block deselecting a role that still has centre(s)
                                                            // attached -- the centre(s) must be removed first via
                                                            // the "Centres Attached to Roles" section below.
                                                            const blockedOnCentres = values.fc_a_role.filter((roleId: number) => {
                                                                if (newIds.includes(roleId)) return false;
                                                                const group = centreGroups.find((g) => g.roleId === roleId);
                                                                return group && group.centres.length > 0;
                                                            });
                                                            // Same block for Franchise Owner -- that attachment lives
                                                            // in user_franchise_roles, removable via the "Franchises
                                                            // Attached to Roles" section below (unassignUserFromFranchise).
                                                            const blockedOnFranchises = values.fc_a_role.filter((roleId: number) => {
                                                                if (newIds.includes(roleId)) return false;
                                                                if (blockedOnCentres.includes(roleId)) return false;
                                                                const group = franchiseGroups.find((g) => g.roleId === roleId);
                                                                return group && group.franchises.length > 0;
                                                            });
                                                            if (blockedOnCentres.length > 0 || blockedOnFranchises.length > 0) {
                                                                blockedOnCentres.forEach((roleId: number) => {
                                                                    const roleName = FCroles.find((r: any) => r.id === roleId)?.name || 'this role';
                                                                    const count = centreGroups.find((g) => g.roleId === roleId)?.centres.length || 0;
                                                                    toast.error(`Cannot remove this role. ${roleName} - attached to ${count} centre${count === 1 ? '' : 's'}. Detach the user from those first, then remove the role.`);
                                                                });
                                                                blockedOnFranchises.forEach((roleId: number) => {
                                                                    const roleName = FCroles.find((r: any) => r.id === roleId)?.name || 'this role';
                                                                    const count = franchiseGroups.find((g) => g.roleId === roleId)?.franchises.length || 0;
                                                                    toast.error(`Cannot remove this role. ${roleName} - attached to ${count} franchise${count === 1 ? '' : 's'}. Detach the user from those first, then remove the role.`);
                                                                });
                                                                setFieldValue("fc_a_role", [...newIds, ...blockedOnCentres, ...blockedOnFranchises]);
                                                                return;
                                                            }
                                                            setFieldValue("fc_a_role", newIds);
                                                        }}
                                                        value={
                                                            FCroles?.length
                                                            ? FCroles.filter((obj: any) =>
                                                                values.fc_a_role.includes(obj.id)
                                                                )
                                                            : []
                                                        } // set selected values
                                                    />
                                                    {(touched.fc_a_role && errors.fc_a_role) && (
                                                        <div className="text-danger">
                                                            {errors.fc_a_role}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {(centreGroups.length > 0 || franchiseGroups.length > 0) && (
                                        <div className="row mt-3">
                                            {centreGroups.length > 0 && (
                                                <div className="col-sm-6">
                                                    <hr />
                                                    <label><strong>Centres Attached to Roles</strong></label>
                                                    {centreGroups.map((group) => (
                                                        <div key={group.roleId} className="mb-2">
                                                            <div className="text-muted small">{group.roleName}:</div>
                                                            <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
                                                                {group.centres.map((c: any) => (
                                                                    <span key={c.centre_id} className="badge badge-light border mr-1 d-inline-flex align-items-center">
                                                                        {c.name}
                                                                        <a
                                                                            href='#'
                                                                            className="ml-1 text-danger"
                                                                            title={`Remove ${c.name} from ${group.roleName}`}
                                                                            onClick={(e) => { e.preventDefault(); handleRemoveCentre(c, group.roleId, group.roleName); }}
                                                                        >
                                                                            &times;
                                                                        </a>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {franchiseGroups.length > 0 && (
                                                <div className="col-sm-6">
                                                    <hr />
                                                    <label><strong>Franchises Attached to Roles</strong></label>
                                                    {franchiseGroups.map((group) => (
                                                        <div key={group.roleId} className="mb-2">
                                                            <div className="text-muted small">{group.roleName}:</div>
                                                            <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
                                                                {group.franchises.map((f: any) => (
                                                                    <span key={f.id} className="badge badge-light border mr-1 d-inline-flex align-items-center">
                                                                        {f.name}
                                                                        <a
                                                                            href='#'
                                                                            className="ml-1 text-danger"
                                                                            title={`Remove ${f.name} from ${group.roleName}`}
                                                                            onClick={(e) => { e.preventDefault(); handleRemoveFranchise(f, group.roleId, group.roleName); }}
                                                                        >
                                                                            &times;
                                                                        </a>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="card-footer">
                                    <div className='col-md-12'>
                                        <Button type="submit" isLoading={isLoading} className="btn btn-primary mr-2">Update </Button>
                                        <Link className="btn btn-default" to='/users'>Close</Link>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>

    </div>
  )
}

export default EditUser;